import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { NumericTextBox, Input } from "@progress/kendo-react-inputs";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import { useAddDealItem, useDealItems } from "src/dm/hooks/useDealItems";
import { useRetailItemSearch } from "src/dm/hooks/useRetailItems";
import { useAddByFamily } from "src/dm/hooks/useAddByFamily";
import { useAddRewardItem } from "src/dm/hooks/useDealRewardSub";
import {
  fetchRetailItemFamilies,
  type RetailItemFamily,
} from "src/dm/hooks/useRetailItemFamilies";
import AddItemFamilyPrompt, {
  type FamilyPromptEntry,
} from "src/dm/components/AddItemFamilyPrompt/AddItemFamilyPrompt";
import IncompleteFamiliesModal from "src/dm/components/AddItemFamilyPrompt/IncompleteFamiliesModal";
import {
  useFamiliesLookup,
  useGroupsLookup,
  useItemStatusesLookup,
  usePriceGroupsLookup,
  useSubgroupsLookup,
  useVendorsLookup,
  type CodeLabelRow,
} from "src/dm/hooks/useLookups";
import { DealItemWriteRequest } from "src/dm/interfaces/DealItem";
import { RetailItem, RetailItemSearchCriteria } from "src/dm/interfaces/RetailItem";
import * as s from "./AddItem.css";

/**
 * Full-page Add Item screen — replaces the legacy modal that lived inside
 * <DealItems>. Mirrors `Item/SearchItem.aspx` (cascading-dropdown criteria
 * panel, distinct vendor / family / group / sub-group / price-group / item-
 * status pickers, results grid with per-row "Add to deal").
 *
 * Family-popup flow:
 *   - We REUSE `<AddItemFamilyPrompt>` and `<IncompleteFamiliesModal>` from
 *     the prior round. They stay as modals because they're confirmations on
 *     top of the search workflow; only the search itself moved to a page.
 *
 * UX departure from legacy:
 *   - After a successful add, we STAY on this page so the user can chain
 *     adds. Legacy returns to ViewDealItems. A "Back to items" link and a
 *     "Done" button live in the breadcrumb row for an explicit exit.
 *   - A session counter ("N items added this session") sits in the
 *     breadcrumb row so the user has visible progress feedback.
 */

const emptyCriteria = (): RetailItemSearchCriteria => ({
  retailItemNumber: null,
  upc: null,
  caseUpc: null,
  whitemNumber: null,
  description: null,
  pack: null,
  size: null,
  vendorNumber: null,
  familyCode: null,
  groupCode: null,
  subgroupCode: null,
  priceGroupCode: null,
  itemStatus: null,
  pageSize: 100,
});

// Sentinel item that lets the user clear a DropDownList back to "all".
// Kendo's DropDownList doesn't render a null/empty data row by default, so
// we prepend this one and translate it back to null on change.
const ANY_OPTION: CodeLabelRow = { code: "", label: "Any" };

const AddItem = () => {
  const deal = useDealContext();
  const dealId = deal.dealId;
  const lookupScope = useMemo(
    () => ({ catMgrCode: deal.categoryManagerCode, banner: deal.bannerCode }),
    [deal.categoryManagerCode, deal.bannerCode]
  );

  // "Add to reward" mode — opened from EditRewardItems via
  // `?addToReward=true`. When set, each successful deal-item add is
  // followed by a reward-items POST so the same item also gets attached
  // to the deal's (singular) reward. DealReward is 1:1 with Deal so we
  // no longer need to carry a reward id around — the API resolves it
  // server-side from dealId. audit/26-gap-closure-verification.md §3 item 9.
  const [searchParams] = useSearchParams();
  const isRewardMode = searchParams.get("addToReward") === "true";

  const { data: dealItems } = useDealItems(dealId);
  const add = useAddDealItem(dealId);
  const search = useRetailItemSearch();
  const addByFamily = useAddByFamily(dealId);
  // Reward-add mutation. Always created (the hook is cheap even when
  // unused) so the hook order is stable across renders. It's only
  // *invoked* in reward mode.
  const addRewardItem = useAddRewardItem(dealId);

  // ---- Cascading-dropdown lookups ----------------------------------------
  // Vendor + Family use server-filtered typeaheads. Group / Sub-Group /
  // Price-Group / Item Status are small enough for an in-memory DropDown.
  const [vendorFilter, setVendorFilter] = useState<string>("");
  const [familyFilter, setFamilyFilter] = useState<string>("");
  const vendors = useVendorsLookup(lookupScope, vendorFilter);
  const families = useFamiliesLookup(lookupScope, familyFilter);
  const groups = useGroupsLookup(lookupScope);
  // Subgroup lookup cascades off the picked group (legacy didn't cascade
  // but the SPA improvement is obvious).
  const [criteria, setCriteria] = useState<RetailItemSearchCriteria>(emptyCriteria);
  const subgroups = useSubgroupsLookup(lookupScope, criteria.groupCode ?? null);
  const priceGroups = usePriceGroupsLookup(lookupScope);
  const itemStatuses = useItemStatusesLookup();

  // ---- Result + session feedback ----------------------------------------
  const [results, setResults] = useState<RetailItem[]>([]);
  const [searchInfo, setSearchInfo] = useState<string>("");
  const [pendingItemNumber, setPendingItemNumber] = useState<number | null>(null);
  /** Running counter of how many adds the user has done on THIS page mount. */
  const [sessionAddedCount, setSessionAddedCount] = useState(0);
  /** Briefly displayed "Retail item N added" / family add banner. */
  const [lastAddedNotice, setLastAddedNotice] = useState<string | null>(null);
  const [familyNotice, setFamilyNotice] = useState<string | null>(null);
  /** Legacy "Add whole family?" prompt state. */
  const [familyPrompt, setFamilyPrompt] = useState<{
    item: RetailItem;
    entries: FamilyPromptEntry[];
  } | null>(null);
  /** Legacy "incomplete families" follow-up popup state. */
  const [incompleteFamilyCodes, setIncompleteFamilyCodes] = useState<number[] | null>(null);

  const existingRetailItemNumbers = useMemo(() => {
    const out = new Set<number>();
    (dealItems ?? []).forEach((d) => out.add(d.retailItemNumber));
    return out;
  }, [dealItems]);

  const setField = <K extends keyof RetailItemSearchCriteria>(
    key: K,
    value: RetailItemSearchCriteria[K]
  ) => setCriteria((p) => ({ ...p, [key]: value }));

  // Wrap the typed-numeric-code DropDownList changes so we translate the
  // ANY_OPTION sentinel back to null. Same pattern for each dropdown.
  const onDropdownChange =
    (k: keyof RetailItemSearchCriteria) =>
    (e: DropDownListChangeEvent) => {
      const code = (e.value as CodeLabelRow | null)?.code ?? "";
      if (!code) {
        setField(k, null as never);
        return;
      }
      // numeric for everything except itemStatus
      if (k === "itemStatus") setField(k, code as never);
      else setField(k, Number(code) as never);
    };

  // For Group, also clear subgroup so the cascade behaves predictably.
  const onGroupChange = (e: DropDownListChangeEvent) => {
    const code = (e.value as CodeLabelRow | null)?.code ?? "";
    setCriteria((p) => ({
      ...p,
      groupCode: code ? Number(code) : null,
      subgroupCode: null,
    }));
  };

  // AutoComplete is a free-text input. Every keystroke fires onChange with
  // the current input string. We:
  //   1. Update the local filter so the useVendorsLookup hook re-queries
  //      the server with the new search term (refetches with debounce
  //      provided by react-query's natural batching).
  //   2. Try to match the typed string against a loaded row — if the user
  //      picked one from the dropdown (label exact match) or typed the
  //      raw code, set the corresponding numeric criteria. Otherwise fall
  //      back to parsing the input as a number so manual vendor-number
  //      entry still works (legacy txtVendor used a free numeric input).
  const onVendorChange = (e: AutoCompleteChangeEvent) => {
    const text = String(e.value ?? "").trim();
    setVendorFilter(text);
    if (!text) {
      setField("vendorNumber", null);
      return;
    }
    const match = (vendors.data?.items ?? []).find(
      (v) => v.label.toLowerCase() === text.toLowerCase() || v.code === text
    );
    if (match) setField("vendorNumber", Number(match.code));
    else {
      const asNum = Number(text);
      setField("vendorNumber", Number.isFinite(asNum) && asNum > 0 ? asNum : null);
    }
  };

  const onFamilyChange = (e: AutoCompleteChangeEvent) => {
    const text = String(e.value ?? "").trim();
    setFamilyFilter(text);
    if (!text) {
      setField("familyCode", null);
      return;
    }
    const match = (families.data?.items ?? []).find(
      (f) => f.label.toLowerCase() === text.toLowerCase() || f.code === text
    );
    if (match) setField("familyCode", Number(match.code));
    else {
      const asNum = Number(text);
      setField("familyCode", Number.isFinite(asNum) && asNum > 0 ? asNum : null);
    }
  };

  // Helpers to compute the currently-selected DropDownList row so the
  // control displays the chosen code (Kendo wants the data row, not the value).
  const findRow = (rows: CodeLabelRow[] | undefined, code: number | string | null | undefined) => {
    if (code == null || code === "") return ANY_OPTION;
    const s = String(code);
    return rows?.find((r) => r.code === s) ?? ANY_OPTION;
  };

  const runSearch = async () => {
    try {
      const rows = await search.mutateAsync(criteria);
      setResults(rows);
      setSearchInfo(rows.length === 0 ? "Your search returned no items." : `Results: ${rows.length}`);
    } catch (err) {
      setResults([]);
      setSearchInfo(err instanceof Error ? err.message : "Search failed");
    }
  };

  const resetForm = () => {
    setCriteria(emptyCriteria());
    setVendorFilter("");
    setFamilyFilter("");
    setResults([]);
    setSearchInfo("");
  };

  const performSingleAdd = async (row: RetailItem) => {
    if (!row.retailItemNumber) return;
    const body: DealItemWriteRequest = {
      retailItemNumber: row.retailItemNumber,
      priceGroupCode: row.priceGroupCode ?? 1,
      supplierNumber: row.supplierNumber ?? null,
      familyCode: row.familyCode ?? null,
      warehouseFacilityNumber: row.warehouseFacilityNumber ?? null,
      // DITM_BEF_I_EXCL and DITM_BEF_IU_FMLY_CD Oracle triggers overwrite
      // these on insert from the matching RETAIL_ITEM row.
      excludeAdPlan: false,
      excludeStoreAdPlan: false,
      addedByFamily: false,
      eligibleForSecondary: false,
    };
    try {
      setPendingItemNumber(row.retailItemNumber);
      // In reward-mode, the item might ALREADY be on the deal — we still
      // want to attach it to the reward. Look it up first; only POST the
      // deal-item if it's missing. This mirrors legacy AddRewardItem.aspx,
      // which silently no-ops the deal-item insert when one already exists.
      let dealItemId: number | undefined;
      if (isRewardMode) {
        dealItemId = (dealItems ?? []).find(
          (d) => d.retailItemNumber === row.retailItemNumber,
        )?.dealItemId;
      }
      if (!dealItemId) {
        const added = await add.mutateAsync(body);
        dealItemId = added.dealItemId;
        setSessionAddedCount((n) => n + 1);
      }
      if (isRewardMode && dealItemId) {
        // Chain a reward-items POST so the same item attaches to the
        // reward. Type defaults to mandatory ("M") — the user can change
        // it from the EditRewardItems grid afterwards.
        await addRewardItem.mutateAsync({
          rewardItemTypeCode: "M",
          rewardItemGroupId: null,
          dealItemId,
          addedByFamily: false,
          scanQuantity: null,
        });
        setLastAddedNotice(
          `Retail item ${row.retailItemNumber} added to deal & reward.`,
        );
      } else {
        setLastAddedNotice(
          `Retail item ${row.retailItemNumber} added to the deal.`,
        );
      }
    } finally {
      setPendingItemNumber(null);
    }
  };

  const performFamilyAdd = async (familyCode: number) => {
    try {
      setPendingItemNumber(0);
      const result = await addByFamily.mutateAsync({
        familyCode,
        includeAllSiblings: true,
      });
      const added = result.addedDealItemIds.length;
      const skipped = result.skippedRetailItemNumbers.length;
      const parts: string[] = [];
      parts.push(`${added} item${added === 1 ? "" : "s"} added`);
      if (skipped > 0) parts.push(`${skipped} already on deal`);
      setFamilyNotice(`Family ${familyCode}: ${parts.join(", ")}.`);
      setSessionAddedCount((n) => n + added);
      if (result.incompleteFamilies.length > 0) {
        setIncompleteFamilyCodes(result.incompleteFamilies);
      }
    } finally {
      setPendingItemNumber(null);
    }
  };

  // Mirror SearchItem.aspx.cs.btnAddSelectedItemsToDeal_Click — see the
  // matching comment in the original DealItems.tsx for the full step list.
  const addToDeal = async (row: RetailItem) => {
    if (!row.retailItemNumber) return;
    setLastAddedNotice(null);
    setFamilyNotice(null);

    // In reward-mode, skip the "add whole family?" prompt — the reward
    // flow targets a specific item (or a small set) and family bulk-add
    // would surprise the user. They can still pick a family in the tree
    // picker on EditRewardItems directly.
    if (isRewardMode || !row.familyCode) {
      await performSingleAdd(row);
      return;
    }

    let familiesForItem: RetailItemFamily[];
    try {
      setPendingItemNumber(row.retailItemNumber);
      familiesForItem = await fetchRetailItemFamilies(row.retailItemNumber);
    } catch {
      await performSingleAdd(row);
      return;
    } finally {
      setPendingItemNumber(null);
    }

    if (familiesForItem.length === 0) {
      await performSingleAdd(row);
      return;
    }

    const onDealRitemNos = new Set((dealItems ?? []).map((d) => d.retailItemNumber));
    const entries: FamilyPromptEntry[] = familiesForItem.map((f) => {
      const missing = f.memberRetailItemNumbers.filter(
        (n) => !onDealRitemNos.has(n) && n !== row.retailItemNumber
      ).length;
      return { family: f, missingSiblingCount: missing };
    });

    if (entries.every((e) => e.missingSiblingCount === 0)) {
      await performSingleAdd(row);
      return;
    }

    setFamilyPrompt({ item: row, entries });
  };

  const cancelFamilyPrompt = () => setFamilyPrompt(null);
  const acceptFamilyPromptAddSingle = async () => {
    if (!familyPrompt) return;
    const row = familyPrompt.item;
    setFamilyPrompt(null);
    await performSingleAdd(row);
  };
  const acceptFamilyPromptAddFamily = async (familyCode: number) => {
    if (!familyPrompt) return;
    setFamilyPrompt(null);
    await performFamilyAdd(familyCode);
  };

  const SearchAddCell = (props: GridCellProps) => {
    const row = props.dataItem as RetailItem;
    const num = row.retailItemNumber ?? 0;
    const alreadyOnDeal = num > 0 && existingRetailItemNumbers.has(num);
    const isPending = pendingItemNumber === num;
    // In reward-mode, "already on the deal" is fine — we still want to
    // attach the existing deal-item to the reward — so the button stays
    // enabled and just changes label. In normal mode the button disables.
    const buttonDisabled = isRewardMode
      ? isPending || !num
      : alreadyOnDeal || isPending || !num;
    const buttonLabel = isPending
      ? "Adding…"
      : isRewardMode
        ? alreadyOnDeal
          ? "Add to reward"
          : "Add to deal + reward"
        : alreadyOnDeal
          ? "On deal"
          : "Add to deal";
    return (
      <td>
        <Button
          type="button"
          themeColor="primary"
          size="small"
          fillMode="flat"
          disabled={buttonDisabled}
          onClick={() => addToDeal(row)}
        >
          {buttonLabel}
        </Button>
      </td>
    );
  };

  // Build the dropdown row list for each lookup (Any + items).
  const vendorRows = vendors.data?.items ?? [];
  const familyRows = families.data?.items ?? [];
  const groupRows = useMemo(
    () => [ANY_OPTION, ...(groups.data?.items ?? [])],
    [groups.data]
  );
  const subgroupRows = useMemo(
    () => [ANY_OPTION, ...(subgroups.data?.items ?? [])],
    [subgroups.data]
  );
  const priceGroupRows = useMemo(
    () => [ANY_OPTION, ...(priceGroups.data?.items ?? [])],
    [priceGroups.data]
  );
  const itemStatusRows = useMemo(
    () => [ANY_OPTION, ...(itemStatuses.data?.items ?? [])],
    [itemStatuses.data]
  );

  // Collect any warning strings that the lookup endpoints surfaced (eg
  // RP_ITEM_AUTH_BANNERS_MV not granted to this user). Render once at the
  // top so we don't repeat the same message six times.
  const warnings = useMemo(() => {
    const list = [
      vendors.data?.warning,
      families.data?.warning,
      groups.data?.warning,
      subgroups.data?.warning,
      priceGroups.data?.warning,
      itemStatuses.data?.warning,
    ].filter((w): w is string => !!w);
    // De-dupe — the same MV powers all of them so we'd otherwise repeat.
    return Array.from(new Set(list));
  }, [vendors.data, families.data, groups.data, subgroups.data, priceGroups.data, itemStatuses.data]);

  return (
    <div className={s.page}>
      <div className={s.breadcrumbRow}>
        <Link
          to={
            isRewardMode
              ? `/dm/deals/${dealId}/reward/items`
              : `/dm/deals/${dealId}/items`
          }
          className={s.backLink}
        >
          ← Back to {isRewardMode ? "reward items" : "items"}
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span className={s.sessionCounter} title="Items you've added to this deal since opening Add Item.">
            {sessionAddedCount} added this session
          </span>
          <Link
            to={
              isRewardMode
                ? `/dm/deals/${dealId}/reward/items`
                : `/dm/deals/${dealId}/items`
            }
          >
            <Button themeColor="primary" fillMode="outline">Done</Button>
          </Link>
        </div>
      </div>

      <h2 style={{ margin: 0 }}>
        {isRewardMode
          ? `Add items to reward (deal ${dealId})`
          : `Add items to deal ${dealId}`}
      </h2>
      {isRewardMode && (
        <div
          className={s.noticeInfo}
          role="status"
          style={{ marginTop: "0.5rem" }}
        >
          <strong>Adding to this deal's reward.</strong> Each chosen item is
          added to the deal (if missing) and to the reward in one step.
        </div>
      )}

      {warnings.length > 0 && (
        <div className={s.noticeWarning} role="status">
          <strong>Heads up:</strong>
          <span>{warnings.join(" ")}</span>
        </div>
      )}

      <div className={s.card}>
        <h3 className={s.sectionTitle}>Search criteria</h3>
        <div className={s.triggerNote} style={{ marginBottom: "0.75rem" }}>
          When you click "Add to deal" on a row whose family has siblings
          not yet on the deal, the legacy <em>Add whole family?</em> prompt
          will open. Family code and "Exclude Ad Plan" / "Exclude Store Ad
          Plan" are controlled by Oracle triggers{" "}
          <code>DITM_BEF_IU_FMLY_CD</code> and <code>DITM_BEF_I_EXCL</code>{" "}
          and cannot be edited from the UI on insert.
        </div>

        <div className={s.criteriaGrid}>
          <label>
            <span className={s.fieldLabel}>Description (contains)</span>
            <Input
              value={criteria.description ?? ""}
              onChange={(e) => setField("description", String(e.target.value ?? "") || null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Item UPC</span>
            <NumericTextBox
              spinners={false}
              format="#"
              value={criteria.upc ?? null}
              onChange={(e) => setField("upc", e.value ?? null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Case UPC</span>
            <NumericTextBox
              spinners={false}
              format="#"
              value={criteria.caseUpc ?? null}
              onChange={(e) => setField("caseUpc", e.value ?? null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Retail Item #</span>
            <NumericTextBox
              spinners={false}
              format="#"
              value={criteria.retailItemNumber ?? null}
              onChange={(e) => setField("retailItemNumber", e.value ?? null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Item # (check digit)</span>
            <NumericTextBox
              spinners={false}
              format="#"
              value={criteria.whitemNumber ?? null}
              onChange={(e) => setField("whitemNumber", e.value ?? null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Pack</span>
            <NumericTextBox
              spinners={false}
              value={criteria.pack ?? null}
              onChange={(e) => setField("pack", e.value ?? null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Size</span>
            <Input
              value={criteria.size ?? ""}
              onChange={(e) => setField("size", String(e.target.value ?? "") || null)}
            />
          </label>

          <label>
            <span className={s.fieldLabel}>Vendor {vendors.isFetching && "…"}</span>
            <AutoComplete
              data={vendorRows.map((v) => v.label)}
              value={vendorFilter}
              onChange={onVendorChange}
              loading={vendors.isFetching}
              placeholder="Type vendor name or number"
            />
          </label>

          <label>
            <span className={s.fieldLabel}>Family {families.isFetching && "…"}</span>
            <AutoComplete
              data={familyRows.map((f) => f.label)}
              value={familyFilter}
              onChange={onFamilyChange}
              loading={families.isFetching}
              placeholder="Type family code or description"
            />
          </label>

          <label>
            <span className={s.fieldLabel}>Group</span>
            <DropDownList
              data={groupRows}
              textField="label"
              dataItemKey="code"
              value={findRow(groupRows, criteria.groupCode)}
              onChange={onGroupChange}
              loading={groups.isLoading}
            />
          </label>

          <label>
            <span className={s.fieldLabel}>Sub-Group</span>
            <DropDownList
              data={subgroupRows}
              textField="label"
              dataItemKey="code"
              value={findRow(subgroupRows, criteria.subgroupCode)}
              onChange={onDropdownChange("subgroupCode")}
              loading={subgroups.isLoading}
              disabled={!criteria.groupCode && subgroupRows.length <= 1}
              title={
                criteria.groupCode
                  ? `Sub-groups of group ${criteria.groupCode}`
                  : "Pick a Group first, or leave both blank"
              }
            />
          </label>

          <label>
            <span className={s.fieldLabel}>Price Group</span>
            <DropDownList
              data={priceGroupRows}
              textField="label"
              dataItemKey="code"
              value={findRow(priceGroupRows, criteria.priceGroupCode)}
              onChange={onDropdownChange("priceGroupCode")}
              loading={priceGroups.isLoading}
            />
          </label>

          <label>
            <span className={s.fieldLabel}>Item Status</span>
            <DropDownList
              data={itemStatusRows}
              textField="label"
              dataItemKey="code"
              value={findRow(itemStatusRows, criteria.itemStatus)}
              onChange={onDropdownChange("itemStatus")}
              loading={itemStatuses.isLoading}
            />
          </label>
        </div>

        <div className={s.actionsRow}>
          <Button themeColor="primary" onClick={runSearch} disabled={search.isPending}>
            {search.isPending ? "Searching…" : "Search"}
          </Button>
          <Button onClick={resetForm} disabled={search.isPending}>
            Reset
          </Button>
          {searchInfo && <span className={s.resultsInfo}>{searchInfo}</span>}
        </div>
      </div>

      {(lastAddedNotice || familyNotice) && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {lastAddedNotice && (
            <div className={s.noticeInfo} role="status">{lastAddedNotice}</div>
          )}
          {familyNotice && (
            <div className={s.noticeSuccess} role="status">{familyNotice}</div>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div className={s.card}>
          <h3 className={s.sectionTitle}>Search results</h3>
          <Grid data={results} style={{ maxHeight: 480 }}>
            <GridColumn field="retailItemNumber" title="Retail #" width="110px" />
            <GridColumn field="whitemNumberWithCheckDigit" title="Item # (cd)" width="110px" />
            <GridColumn field="upc" title="UPC" width="140px" />
            <GridColumn field="itemDescription" title="Description" />
            <GridColumn field="vendorNumber" title="Vendor #" width="100px" />
            <GridColumn field="vendorName" title="Vendor" width="180px" />
            <GridColumn field="itemPack" title="Pack" width="70px" />
            <GridColumn field="itemSize" title="Size" width="80px" />
            <GridColumn field="familyCode" title="Family" width="80px" />
            <GridColumn field="itemStatus" title="Stat" width="60px" />
            <GridColumn title="" width="110px" cells={{ data: SearchAddCell }} />
          </Grid>
        </div>
      )}

      {add.isError && (
        <pre style={{ color: "#b91c1c", margin: "0.5rem 0", fontSize: "0.8rem" }}>
          {add.error instanceof Error ? add.error.message : "Failed"}
        </pre>
      )}

      {familyPrompt && (
        <AddItemFamilyPrompt
          retailItemNumber={familyPrompt.item.retailItemNumber ?? 0}
          entries={familyPrompt.entries}
          onCancel={cancelFamilyPrompt}
          onAddSingleItem={acceptFamilyPromptAddSingle}
          onAddFamily={acceptFamilyPromptAddFamily}
          busy={add.isPending || addByFamily.isPending}
        />
      )}

      {incompleteFamilyCodes && incompleteFamilyCodes.length > 0 && (
        <IncompleteFamiliesModal
          familyCodes={incompleteFamilyCodes}
          onClose={() => setIncompleteFamilyCodes(null)}
        />
      )}
    </div>
  );
};

export default AddItem;
